import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '../services/chatService';
import {
  fetchClientNotifications,
  fetchClientUnreadCount,
  markAllClientNotificationsRead,
  markClientNotificationRead,
  type ClientNotification,
  type ClientNotificationFilters,
} from '../services/clientNotificationService';

interface NotificationContextType {
  notifications: ClientNotification[];
  unreadCount: number;
  loading: boolean;
  loadNotifications: (
    filters?: ClientNotificationFilters,
    options?: { force?: boolean }
  ) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const UNREAD_REFRESH_MS = 60_000;
const LIST_REFRESH_MS = 15_000;

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastUnreadFetchRef = useRef(0);
  const lastListFetchRef = useRef(0);
  const unreadInFlightRef = useRef(false);
  const listInFlightRef = useRef(false);

  const refreshUnreadCount = useCallback(async (force = false) => {
    if (!isAuthenticated || unreadInFlightRef.current) return;
    const now = Date.now();
    if (!force && now - lastUnreadFetchRef.current < UNREAD_REFRESH_MS) return;

    unreadInFlightRef.current = true;
    try {
      const count = await fetchClientUnreadCount();
      setUnreadCount(count);
      lastUnreadFetchRef.current = Date.now();
    } catch (error) {
      console.error('[Notifications] unread count failed:', error);
    } finally {
      unreadInFlightRef.current = false;
    }
  }, [isAuthenticated]);

  const loadNotifications = useCallback(
    async (filters: ClientNotificationFilters = {}, options?: { force?: boolean }) => {
      if (!isAuthenticated || listInFlightRef.current) return;
      const force = options?.force === true;
      const now = Date.now();
      if (!force && notifications.length > 0 && now - lastListFetchRef.current < LIST_REFRESH_MS) {
        return;
      }

      listInFlightRef.current = true;
      try {
        setLoading(true);
        const result = await fetchClientNotifications({ limit: 30, ...filters });
        setNotifications(result.notifications);
        setUnreadCount(result.unreadCount);
        lastListFetchRef.current = Date.now();
        lastUnreadFetchRef.current = Date.now();
      } catch (error) {
        console.error('[Notifications] load failed:', error);
      } finally {
        setLoading(false);
        listInFlightRef.current = false;
      }
    },
    [isAuthenticated, notifications.length]
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    await markClientNotificationRead(notificationId);
    setNotifications((prev) =>
      prev.map((n) => (n._id === notificationId ? { ...n, status: 'read' as const } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await markAllClientNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
    setUnreadCount(0);
    lastUnreadFetchRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      lastUnreadFetchRef.current = 0;
      lastListFetchRef.current = 0;
      return;
    }
    void refreshUnreadCount(true);
  }, [isAuthenticated, refreshUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onNew = (payload: { notification?: ClientNotification }) => {
      const notification = payload?.notification;
      if (!notification?._id) return;
      setNotifications((prev) => {
        if (prev.some((n) => n._id === notification._id)) return prev;
        return [notification, ...prev];
      });
      if (notification.status === 'unread') {
        setUnreadCount((prev) => prev + 1);
      }
    };

    const attach = () => {
      const socket = chatService.getSocket();
      if (!socket) return;
      socket.off('client:notification:new', onNew);
      socket.on('client:notification:new', onNew);
    };

    const socket = chatService.getSocket();
    if (socket?.connected) {
      attach();
    } else if (socket) {
      socket.once('connect', attach);
    }

    return () => {
      const activeSocket = chatService.getSocket();
      activeSocket?.off('client:notification:new', onNew);
      activeSocket?.off('connect', attach);
    };
  }, [isAuthenticated]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        loadNotifications,
        refreshUnreadCount,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
